/**
 * WebSocket client for server-authoritative battle at /ws/battle.
 */

import { getWebSocketUrl } from '../utils/wsUrl';
import { translateNamesDeep } from '../utils/gameNameI18n';

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10000;
const REQUEST_TIMEOUT_MS = 45000;

const getBattleSocketUrl = () => {
  if (process.env.REACT_APP_BATTLE_WS_URL) {
    return process.env.REACT_APP_BATTLE_WS_URL;
  }
  return getWebSocketUrl('/ws/battle');
};

export function createBattleSocket() {
  const listeners = {};
  let ws = null;
  let shouldReconnect = false;
  let socketGeneration = 0;
  let reconnectDelay = RECONNECT_BASE_MS;
  let reconnectTimer = null;
  let reconnectAttempt = 0;
  const pending = new Map();
  let reqCounter = 0;
  let sendChain = Promise.resolve();

  const emit = (event, data) => {
    (listeners[event] || []).forEach((fn) => {
      try {
        fn(data);
      } catch (_) {}
    });
  };

  const on = (event, fn) => {
    if (!listeners[event]) listeners[event] = [];
    listeners[event].push(fn);
    return () => off(event, fn);
  };

  const off = (event, fn) => {
    if (!listeners[event]) return;
    listeners[event] = listeners[event].filter((f) => f !== fn);
  };

  const rejectPending = (message) => {
    pending.forEach(({ reject, timeoutId }) => {
      if (timeoutId) clearTimeout(timeoutId);
      reject(new Error(message));
    });
    pending.clear();
  };

  const handleMessage = (raw) => {
    let env;
    try {
      env = JSON.parse(raw.data);
    } catch {
      return;
    }

    const { type, reqId, payload } = env;
    let parsed = payload;
    if (typeof payload === 'string') {
      try {
        parsed = JSON.parse(payload);
      } catch {
        parsed = payload;
      }
    }
    // Canavar/eşya adları seçili dile çevrilir (yalnızca gösterim).
    translateNamesDeep(parsed);

    if (reqId && pending.has(reqId)) {
      const { resolve, reject, expectType, timeoutId } = pending.get(reqId);
      pending.delete(reqId);
      if (timeoutId) clearTimeout(timeoutId);
      if (type === 'error') {
        const err = new Error(parsed?.message || 'Battle socket error');
        err.code = parsed?.code;
        reject(err);
        return;
      }
      if (expectType && type !== expectType) {
        resolve({ type, payload: parsed });
        return;
      }
      resolve(parsed);
      return;
    }

    if (type === 'error') {
      emit('error', parsed);
      return;
    }

    emit(type, parsed);
  };

  const connect = () => {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const url = getBattleSocketUrl();
    const myGen = ++socketGeneration;
    shouldReconnect = true;
    const socket = new WebSocket(url);
    ws = socket;

    socket.onopen = () => {
      if (myGen !== socketGeneration) {
        socket.close();
        return;
      }
      reconnectDelay = RECONNECT_BASE_MS;
      reconnectAttempt = 0;
      emit('connected');
    };

    socket.onmessage = handleMessage;

    socket.onclose = () => {
      if (ws === socket) {
        ws = null;
      }
      emit('disconnected');
      rejectPending('Server yoğun');
      sendChain = Promise.resolve();
      if (shouldReconnect && myGen === socketGeneration) {
        clearTimeout(reconnectTimer);
        const delay = reconnectDelay;
        reconnectAttempt += 1;
        emit('reconnecting', { attempt: reconnectAttempt, delay });
        reconnectTimer = setTimeout(() => {
          reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
          connect();
        }, delay);
      }
    };

    socket.onerror = () => {
      emit('error', { message: 'Server yoğun' });
    };
  };

  const sendNow = (type, payload = {}, expectType = null) =>
    new Promise((resolve, reject) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket bağlı değil'));
        return;
      }
      const reqId = `${Date.now()}-${++reqCounter}`;
      const timeoutId = setTimeout(() => {
        if (!pending.has(reqId)) return;
        pending.delete(reqId);
        reject(new Error('Savaş sunucusu yanıt vermedi (zaman aşımı)'));
      }, REQUEST_TIMEOUT_MS);
      pending.set(reqId, { resolve, reject, expectType, timeoutId });
      ws.send(JSON.stringify({ type, reqId, payload }));
    });

  const send = (type, payload = {}, expectType = null) => {
    const task = sendChain.then(() => sendNow(type, payload, expectType));
    sendChain = task.catch(() => {});
    return task;
  };

  const close = () => {
    socketGeneration += 1;
    shouldReconnect = false;
    clearTimeout(reconnectTimer);
    rejectPending('connection closed');
    sendChain = Promise.resolve();
    if (!ws) return;
    const socket = ws;
    ws = null;
    if (socket.readyState === WebSocket.OPEN) {
      socket.close();
    }
    // CONNECTING: close() çağırma; socket açılınca disposed olduğu için kendini kapatır.
  };

  return { connect, send, close, on, off, emit };
}

export default createBattleSocket;
